import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, ArrowRight, CalendarDays } from "lucide-react";
import { getActiveCampaignPhase, getCampaignTimeLeft, msUntilNextTransition } from "@/lib/campaign";
import { scrollToId } from "@/lib/scroll";

const CAMPAIGN_FLAG_KEY = "dr_campaign";

/** Marca que o lead chegou pela campanha — lido pelo formulário de cotação. */
export function markCampaignVisit(campaignId) {
    try {
        sessionStorage.setItem(CAMPAIGN_FLAG_KEY, campaignId);
    } catch {
        /* storage indisponível — a cotação funciona normalmente */
    }
}

export function readCampaignVisit() {
    try {
        const id = sessionStorage.getItem(CAMPAIGN_FLAG_KEY) || "";
        // Invalida a marcação se a campanha já encerrou (evita leads marcados fora do período)
        if (id && getActiveCampaignPhase()?.campaignId !== id) {
            sessionStorage.removeItem(CAMPAIGN_FLAG_KEY);
            return "";
        }
        return id;
    } catch {
        return "";
    }
}

const UNIT_LABELS = [
    ["days", "dias"],
    ["hours", "horas"],
    ["minutes", "min"],
    ["seconds", "seg"],
];

function Countdown({ phaseKey }) {
    const [left, setLeft] = useState(() => getCampaignTimeLeft(phaseKey));

    useEffect(() => {
        setLeft(getCampaignTimeLeft(phaseKey));
        const interval = setInterval(() => setLeft(getCampaignTimeLeft(phaseKey)), 1000);
        return () => clearInterval(interval);
    }, [phaseKey]);

    if (!left) return null;

    return (
        <div className="flex flex-col items-center gap-1.5" data-testid="campaign-countdown">
            <div className="flex items-center gap-1.5 sm:gap-2">
                {UNIT_LABELS.map(([unit, label]) => (
                    <div key={unit} className="flex min-w-[52px] flex-col items-center rounded-xl border border-gold/25 bg-white/[0.06] px-2 py-1.5 backdrop-blur-sm">
                        <span className="font-serif text-lg font-semibold tabular-nums text-gold sm:text-xl" data-testid={`countdown-${unit}`}>
                            {String(left[unit]).padStart(2, "0")}
                        </span>
                        <span className="text-[9px] uppercase tracking-[0.14em] text-stone-400">{label}</span>
                    </div>
                ))}
            </div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-stone-400">{left.label}</p>
        </div>
    );
}

/**
 * Banner de campanha promocional (ex.: Black Friday).
 * Aparece, troca de etapa e some sozinho conforme as datas de
 * src/lib/campaign.js — sempre no fuso de Brasília. O CTA rola para o
 * formulário de cotação já existente (nenhum formulário novo) e marca a
 * origem da campanha para o painel administrativo.
 */
export default function CampaignBanner() {
    const [phase, setPhase] = useState(() => getActiveCampaignPhase());

    // Reavalia automaticamente na virada de cada etapa, sem intervenção manual
    useEffect(() => {
        let timer;
        const schedule = () => {
            const ms = msUntilNextTransition();
            if (ms !== null) {
                timer = setTimeout(() => {
                    setPhase(getActiveCampaignPhase());
                    schedule();
                }, Math.min(ms, 2 ** 31 - 1));
            }
        };
        schedule();
        return () => clearTimeout(timer);
    }, []);

    const handleCta = () => {
        if (phase) markCampaignVisit(phase.campaignId);
        scrollToId("cotacao-rapida");
    };

    return (
        <AnimatePresence mode="wait">
            {phase && (
                <motion.section
                    key={phase.key}
                    initial={{ opacity: 0, y: -14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="relative overflow-hidden bg-forest-deep"
                    data-testid="campaign-banner"
                >
                    <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 py-5 text-center sm:px-6 sm:py-6 lg:flex-row lg:justify-between lg:gap-8 lg:text-left">
                        <div className="flex items-center gap-4">
                            <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold sm:flex">
                                <Sparkles className="h-5 w-5" />
                            </span>
                            <div>
                                {phase.datesLabel && (
                                    <p className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-3 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-gold" data-testid="campaign-banner-dates">
                                        <CalendarDays className="h-3 w-3" />
                                        {phase.datesLabel}
                                    </p>
                                )}
                                <p className="campaign-glow font-serif text-xl font-bold tracking-wide text-gold sm:text-2xl lg:text-3xl" data-testid="campaign-banner-title">
                                    {phase.title}
                                </p>
                                <p className="mt-1 text-xs leading-relaxed text-stone-300 sm:text-sm" data-testid="campaign-banner-subtitle">
                                    {phase.subtitle}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col items-center gap-3.5 sm:flex-row sm:gap-6">
                            <Countdown phaseKey={phase.key} />
                            <button
                                type="button"
                                data-testid="campaign-banner-cta"
                                onClick={handleCta}
                                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-gold px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] text-forest-deep shadow-lg shadow-gold/25 transition-all duration-200 hover:bg-sun hover:shadow-gold/40 active:scale-[0.97]"
                            >
                                {phase.cta}
                                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                            </button>
                        </div>
                    </div>
                    <div className="campaign-line-glow h-px bg-gradient-to-r from-transparent via-gold to-transparent" aria-hidden="true" />
                </motion.section>
            )}
        </AnimatePresence>
    );
}
